/**
 * La campanita.
 *
 * QUE LLEGA AQUI, Y POR QUE ESTAS COSAS Y NO OTRAS
 * ------------------------------------------------
 * Solo lo que alguien tiene que MIRAR y sobre lo que puede HACER algo:
 *
 *   - El reporte del dia, a las 18:15 de la tienda. No lo genera este aviso: el
 *     corte se sigue armando cuando se pide, y se sigue pudiendo abrir antes
 *     desde Caja. Lo que agrega es el toquecito a la hora en que la jornada ya
 *     se puede leer completa.
 *   - La entrada de mercancia que un empleado dejo esperando firma. Mientras no
 *     se firme, esa mercancia esta en el mostrador y NO cuenta como existencia.
 *   - Y el desenlace de la que uno mando, que es lo que evita capturarla otra
 *     vez por no saber si se perdio o se rechazo.
 *   - Lo que se esta acabando y lo que ya se acabo, en el momento en que cruza.
 *
 * Lo que pasa todo el dia —cada venta, cada abono— NO llega aqui. Una campanita
 * que suena cincuenta veces se deja de mirar, y entonces el aviso que si
 * importaba tampoco se ve.
 *
 * QUIEN VE QUE NO SE DECIDE EN ESTE ARCHIVO
 * -----------------------------------------
 * Cada aviso guarda en la base el permiso que hay que tener para verlo, y la
 * RPC ya devuelve unicamente lo que a esta persona le toca. Aqui no hay un solo
 * filtro por rol: el cajero no ve el reporte del dia porque la base no se lo
 * manda, no porque esta pantalla lo esconda. Esconder un boton no es control de
 * acceso (§8), y esto no es un boton escondido: es informacion que no viajo.
 *
 * ABRIR UN AVISO LO MARCA LEIDO — Y ABRIR LA BANDEJA TAMBIEN
 * ----------------------------------------------------------
 * La burbuja se apaga al abrir, no al tocar uno por uno. La alternativa
 * —quedarse encendida hasta que alguien pulse "marcar todo"— convierte el
 * numero en ruido permanente en dos dias, y una campanita que siempre dice algo
 * es una campanita que ya no dice nada. Lo que si se conserva es CUALES eran
 * nuevos en esta abierta: se guardan al abrir y siguen con su punto mientras se
 * leen, aunque en la base ya queden marcados.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  ChevronRight,
  FileText,
  PackageCheck,
  PackagePlus,
  PackageX,
  TrendingDown,
} from 'lucide-react';
import {
  formatRelative,
  type Notification,
  type NotificationFeed,
  type NotificationKind,
  type NotificationSeverity,
} from '@abarrotes/shared';
import { ROUTES } from '../routes.js';
import { api } from '../lib/api.js';
import { useRouter } from '../lib/router.js';
import { useStoreId } from '../lib/session.js';
import { requestScreenIntent, type ScreenIntent } from '../lib/screen-intent.js';
import { cx, EmptyState, ErrorState, LoadingBlock, Modal } from './ui.js';

/** Trazo mas fino que el de fabrica, igual que el boton de escanear. */
const BELL_STROKE = 1.75;

export function NotificationBell({ className }: { className?: string }): ReactNode {
  const storeId = useStoreId();
  const queryClient = useQueryClient();
  const { navigate } = useRouter();
  const [open, setOpen] = useState(false);

  const query = useQuery({
    queryKey: ['notifications', storeId],
    queryFn: () => api<NotificationFeed>(`/api/stores/${storeId}/notifications`, {
      query: { limit: 30 },
    }),
    /*
     * Realtime ya invalida esta consulta cuando entra un aviso, asi que esto no
     * es la via principal: es la red debajo. Si el socket se cayo —el WiFi del
     * changarro, el celular que estuvo bloqueado— la burbuja se quedaria
     * congelada sin que nadie lo note, y el reporte de las 18:15 no aparece
     * hasta la siguiente recarga. Cinco minutos son una llamada barata y hacen
     * que el peor caso sea "tarda un rato" en vez de "no llego".
     */
    refetchInterval: 5 * 60_000,
  });

  const markRead = useMutation({
    mutationFn: (ids: number[]) =>
      api(`/api/stores/${storeId}/notifications/read`, { method: 'POST', body: { ids } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const list = query.data?.notifications ?? [];
  const unread = query.data?.unreadCount ?? 0;

  /*
   * Cuales eran nuevos EN ESTA ABIERTA.
   *
   * La base los marca leidos en cuanto se abre la bandeja, asi que sin esto el
   * punto de "sin leer" se apagaria bajo el dedo mientras la persona los lee.
   * Se vacia al cerrar: la proxima vez que abra, lo nuevo vuelve a ser nuevo.
   */
  const freshRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!open || markRead.isPending) return;
    const pendientes = list.filter((n) => !n.read).map((n) => n.id);
    if (pendientes.length === 0) return;
    for (const id of pendientes) freshRef.current.add(id);
    markRead.mutate(pendientes);
    // `markRead` es estable en identidad util; depender del objeto entero
    // volveria a disparar el efecto en cada cambio de estado de la mutacion.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, list]);

  const close = (): void => {
    setOpen(false);
    freshRef.current.clear();
  };

  const openNotification = (item: Notification): void => {
    const target = destinationFor(item);
    close();
    navigate(target.to);
    if (target.intent !== undefined) requestScreenIntent(target.intent);
  };

  // Rojo solo cuando algo se acabo. El resto de los avisos son cosas que hay que
  // mirar, no cosas que esten mal, y gastar el rojo en ellas lo deja sin efecto
  // el dia que si haga falta.
  const urgente = list.some((n) => !n.read && n.severity === 'critical');

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={unread > 0 ? `Avisos: ${unread} sin leer` : 'Avisos'}
        /*
         * DOS MEDIDAS, igual que el boton de escanear: el area que se toca mide
         * 48 px —la guia tactil de Apple y Material— y lo que se PINTA mide 36,
         * que es la altura del selector de sucursal que tiene al lado. Un cuadro
         * de 48 junto a una pastilla de 36 se lee como un error de alineacion.
         *
         * Y el mismo traje que sus vecinos —borde `line` sobre `surface-2`—
         * porque lo que hace que una barra se vea cara es que todas sus piezas
         * hablen igual. El unico color de este rincon es el de la burbuja, que
         * es lo que de verdad informa.
         */
        className={cx('group touch-target grid place-items-center shrink-0', className)}
      >
        <span
          className={cx(
            'relative grid place-items-center size-9 rounded-xl',
            'border border-line bg-surface-2 text-ink',
            'transition-[border-color,transform] duration-150',
            'group-hover:border-line-strong group-active:scale-95',
          )}
        >
          <Bell className="size-[18px]" strokeWidth={BELL_STROKE} />

          {unread > 0 ? (
            /*
             * La burbuja se ASOMA fuera del cuadro y lleva un aro del color del
             * fondo, que es lo que la despega del borde en vez de dejarla
             * pegada encima. `ring-surface` sirve para los dos sitios donde vive
             * la campanita: la barra lateral es `bg-surface` y el encabezado del
             * telefono es `glass`, que es surface con transparencia.
             *
             * Entra con `animate-pop` y se queda quieta. NO parpadea: esto vive
             * en una pantalla que alguien mira ocho horas seguidas, y un aviso
             * que se mueve para siempre se vuelve una molestia y no una señal
             * —es el mismo motivo por el que el latido de "en vivo" es lento—.
             */
            <span
              aria-hidden
              className={cx(
                'animate-pop absolute -top-1.5 -right-1.5 grid place-items-center',
                'min-w-[18px] h-[18px] px-1 rounded-full ring-2 ring-surface',
                'text-[11px] font-bold leading-none tabular',
                urgente ? 'bg-danger text-white' : 'bg-primary text-on-primary',
              )}
            >
              {unread > 99 ? '99+' : unread}
            </span>
          ) : null}
        </span>
      </button>

      <Modal open={open} onClose={close} title="Avisos" size="sm">
        {query.isLoading ? (
          <LoadingBlock label="Cargando avisos..." />
        ) : query.isError ? (
          /* Una lectura que FALLA no es una bandeja VACIA: sin esto, un tropiezo
             de red diria "no hay avisos" y quien esta esperando la firma de una
             entrega concluiria que nadie la mando. */
          <ErrorState
            error={query.error}
            what="tus avisos"
            onRetry={() => void query.refetch()}
            retrying={query.isFetching}
          />
        ) : list.length === 0 ? (
          <EmptyState
            icon={<Bell className="size-8" strokeWidth={BELL_STROKE} />}
            title="Nada nuevo por ahora"
            description="Aqui llega el reporte del dia a las 6:15 pm, las entradas de mercancia que esperan firma, y lo que se esta acabando o ya se acabo."
          />
        ) : (
          /* Misma forma que la lista de clientes: separadores entre renglones y
             el realce del dedo asomandose un poco fuera del relleno de la hoja.
             NO se saca con `-mx-safe-5`: el relleno lateral de la hoja es un
             `max(diseño, sistema)` y no existe su negativo. */
          <ul className="divide-y divide-line">
            {list.map((item) => (
              <li key={item.id}>
                <NotificationRow
                  item={item}
                  fresh={freshRef.current.has(item.id)}
                  onOpen={() => openNotification(item)}
                />
              </li>
            ))}
          </ul>
        )}
      </Modal>
    </>
  );
}

function NotificationRow({
  item,
  fresh,
  onOpen,
}: {
  item: Notification;
  /** Estaba sin leer cuando se abrio la bandeja. */
  fresh: boolean;
  onOpen: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cx(
        'w-full text-left flex items-start gap-3 py-3 -mx-2 px-2 lg:px-3 rounded-lg',
        'transition-colors hover:bg-surface-2 active:bg-surface-3',
      )}
    >
      <span
        className={cx(
          'grid place-items-center size-9 rounded-xl border shrink-0 mt-0.5',
          SEVERITY_BOX[item.severity],
          !fresh && 'opacity-60',
        )}
      >
        {KIND_ICON[item.kind]}
      </span>

      {/* `min-w-0` y `truncate` en el titulo: un nombre de producto largo
          ensancharia la fila por encima de la hoja y arrastraria al resto. El
          CUERPO si parte en varias lineas, porque es la informacion. */}
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span
            className={cx(
              'text-[14px] truncate',
              fresh ? 'font-semibold text-ink' : 'font-medium text-muted',
            )}
          >
            {item.title}
          </span>
          {fresh ? (
            <span aria-label="Sin leer" className="size-2 rounded-full bg-primary shrink-0" />
          ) : null}
        </span>
        <span className="block text-[13px] text-muted mt-0.5 text-pretty">{item.body}</span>
        <span className="block text-[12px] text-subtle mt-1">{formatRelative(item.createdAt)}</span>
      </span>

      <ChevronRight className="size-4 text-subtle shrink-0 mt-3" />
    </button>
  );
}

/**
 * A donde lleva cada aviso.
 *
 * La regla: al destino donde se puede HACER algo con el, no a una pantalla que
 * lo repita. El aviso ademas deja un recado para que la pantalla abra lo que
 * hay que abrir —el visor del corte, el renglon del producto— porque eso vive
 * alla y no en el marco (ver `lib/screen-intent.ts`).
 */
function destinationFor(item: Notification): { to: string; intent?: ScreenIntent } {
  switch (item.kind) {
    // Caja, con el visor del reporte ya abierto.
    case 'daily_report':
      return { to: ROUTES.cash, intent: { kind: 'daily-report' } };
    // La bandeja de entradas por firmar vive en Personal.
    case 'intake_submitted':
      return { to: ROUTES.staff };
    // "Lo que mandaste" encabeza el inventario de quien propone.
    case 'intake_resolved':
      return { to: ROUTES.inventory };
    // Al renglon del producto, que es donde esta el boton de resurtir. Si por
    // lo que sea no viniera el nombre, el inventario entero sigue siendo un
    // destino util.
    case 'low_stock':
    case 'out_of_stock': {
      const name = item.payload['productName'];
      return {
        to: ROUTES.inventory,
        ...(typeof name === 'string' && name !== ''
          ? { intent: { kind: 'find-product', name } as const }
          : {}),
      };
    }
  }
}

const KIND_ICON: Record<NotificationKind, ReactNode> = {
  // El mismo simbolo que el boton "Reporte del dia" de Caja, a proposito: el
  // aviso y el boton abren exactamente lo mismo.
  daily_report: <FileText className="size-[18px]" strokeWidth={BELL_STROKE} />,
  intake_submitted: <PackagePlus className="size-[18px]" strokeWidth={BELL_STROKE} />,
  intake_resolved: <PackageCheck className="size-[18px]" strokeWidth={BELL_STROKE} />,
  low_stock: <TrendingDown className="size-[18px]" strokeWidth={BELL_STROKE} />,
  out_of_stock: <PackageX className="size-[18px]" strokeWidth={BELL_STROKE} />,
};

const SEVERITY_BOX: Record<NotificationSeverity, string> = {
  info: 'bg-info-soft text-info border-info/25',
  warn: 'bg-warn-soft text-warn border-warn/25',
  critical: 'bg-danger-soft text-danger border-danger/25',
};
